'use client';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Field, Form, Formik } from 'formik';
import type { FieldProps } from 'formik';
import { useState } from 'react';
import { inputVariants } from '../../../components/ui/input-variants';
import { cn } from '../../../lib/utils';
import {
  LANDING_EMPHASIS_PRIMARY_COLOR,
  landingContact,
  landingContactAnnualIntakeOptions,
  landingContactFormFields,
  landingContactShelterSystemOptions,
} from '../content/landing-copy';
import {
  landingContactInitialValues,
  landingContactSchema,
  type LandingContactFormValues,
} from '../validation/landing-contact';
import {
  getContactRequestErrorMessage,
  submitContactRequest,
} from '../api/landing-contact-api';

type LandingContactFormProps = {
  className?: string;
};

const fieldInputClass = cn(
  inputVariants({ inputSize: 'lg', fullWidth: true }),
  'border-foreground/20 bg-[#2D2138] text-foreground placeholder:text-foreground/45',
);

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className='text-sm text-destructive' role='alert'>
      {message}
    </p>
  );
}

function TextField({
  label,
  name,
  type = 'text',
  placeholder,
  autoComplete,
}: {
  label: string;
  name: keyof LandingContactFormValues;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-foreground'>{label}</span>
          <input
            {...field}
            id={field.name}
            type={type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className={cn(
              fieldInputClass,
              meta.touched && meta.error && 'border-destructive',
            )}
          />
          <FieldError
            message={meta.touched ? (meta.error as string) : undefined}
          />
        </label>
      )}
    </Field>
  );
}

function SelectField({
  label,
  name,
  placeholder,
  options,
}: {
  label: string;
  name: keyof LandingContactFormValues;
  placeholder: string;
  options: readonly string[];
}) {
  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-foreground'>{label}</span>
          <div className='relative'>
            <select
              {...field}
              id={field.name}
              className={cn(
                fieldInputClass,
                'appearance-none pr-10',
                !field.value && 'text-foreground/45',
                meta.touched && meta.error && 'border-destructive',
              )}
            >
              <option value='' disabled>
                {placeholder}
              </option>
              {options.map((option) => (
                <option key={option} value={option} className='text-foreground'>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className='pointer-events-none absolute top-1/2 right-3 size-5 -translate-y-1/2 text-foreground/60'
              aria-hidden
            />
          </div>
          <FieldError
            message={meta.touched ? (meta.error as string) : undefined}
          />
        </label>
      )}
    </Field>
  );
}

function TextAreaField({
  label,
  name,
  placeholder,
}: {
  label: string;
  name: keyof LandingContactFormValues;
  placeholder?: string;
}) {
  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <label className='flex flex-col gap-1.5 text-sm'>
          <span className='font-medium text-foreground'>{label}</span>
          <textarea
            {...field}
            id={field.name}
            rows={5}
            placeholder={placeholder}
            className={cn(
              fieldInputClass,
              'min-h-[8.5rem] resize-y py-3',
              meta.touched && meta.error && 'border-destructive',
            )}
          />
          <FieldError
            message={meta.touched ? (meta.error as string) : undefined}
          />
        </label>
      )}
    </Field>
  );
}

export function LandingContactForm({ className }: LandingContactFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const placeholders = landingContactFormFields.placeholders;

  if (submitted) {
    return (
      <div className={cn('flex flex-col gap-2', className)} role='status'>
        <p className='font-heading text-lg font-semibold text-foreground'>
          {landingContactFormFields.successTitle}
        </p>
        <p className='text-sm leading-relaxed text-foreground/85 lg:text-base'>
          {landingContact.successMessage}
        </p>
      </div>
    );
  }

  return (
    <Formik
      initialValues={landingContactInitialValues}
      validationSchema={landingContactSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        if (values.botField) {
          setSubmitted(true);
          resetForm();
          return;
        }

        setSubmitError(null);
        try {
          await submitContactRequest(values);
          setSubmitted(true);
          resetForm();
        } catch (err) {
          setSubmitError(getContactRequestErrorMessage(err));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting }) => (
        <Form className={cn('flex flex-col gap-5', className)} noValidate>
          <div className='grid gap-5 sm:grid-cols-2'>
            <TextField
              label={landingContactFormFields.firstName}
              name='firstName'
              placeholder={placeholders.firstName}
              autoComplete='given-name'
            />
            <TextField
              label={landingContactFormFields.lastName}
              name='lastName'
              placeholder={placeholders.lastName}
              autoComplete='family-name'
            />
          </div>

          <TextField
            label={landingContactFormFields.email}
            name='email'
            type='email'
            placeholder={placeholders.email}
            autoComplete='email'
          />

          <TextField
            label={landingContactFormFields.shelterName}
            name='shelterName'
            placeholder={placeholders.shelterName}
            autoComplete='organization'
          />

          <div className='grid gap-5 sm:grid-cols-2'>
            <SelectField
              label={landingContactFormFields.annualIntake}
              name='annualIntake'
              placeholder={placeholders.annualIntake}
              options={landingContactAnnualIntakeOptions}
            />
            <SelectField
              label={landingContactFormFields.shelterSystem}
              name='shelterSystem'
              placeholder={placeholders.shelterSystem}
              options={landingContactShelterSystemOptions}
            />
          </div>

          <TextAreaField
            label={landingContactFormFields.message}
            name='message'
            placeholder={placeholders.message}
          />

          <div
            className='absolute left-[-9999px] h-px w-px overflow-hidden'
            aria-hidden
          >
            <Field name='botField'>
              {({ field }: FieldProps) => (
                <label>
                  Do not fill
                  <input
                    {...field}
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                  />
                </label>
              )}
            </Field>
          </div>

          {submitError ? (
            <p className='text-sm text-destructive' role='alert'>
              {submitError}
            </p>
          ) : null}

          <button
            type='submit'
            disabled={isSubmitting}
            className='mt-1 inline-flex min-h-11 w-full items-center justify-center rounded-xl px-5 text-base font-medium text-primary-foreground transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-60'
            style={{ backgroundColor: LANDING_EMPHASIS_PRIMARY_COLOR }}
          >
            {isSubmitting ? 'Sending…' : landingContact.submitLabel}
          </button>
        </Form>
      )}
    </Formik>
  );
}
